import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  Modal,
  Spin,
  Switch,
  message,
} from '@surecompanies/core_components'
import InputNumber from 'antd/lib/input-number'
import Input from 'antd/lib/input'
import {
  CheckCircleFilled,
  CheckOutlined,
  CloseOutlined,
  InfoCircleOutlined,
  MinusCircleOutlined,
  SendOutlined,
  TableOutlined,
} from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import {
  createOverrideRule,
  updateOverrideRule,
  updateOverrideRuleStatus,
} from '../../PlanOverrides/api/plan-benefit-override-rules-api'
import {
  presignUploadUrl,
  completeUpload,
} from '../../PlanOverrides/api/plan-override-documents-api'
import { uploadToS3 } from '../../PlanOverrides/utils/uploadToS3'
import {
  PlanBenefitOverrideValueSource,
  PlanBenefitOverrideRuleTemplateKey,
  PlanBenefitOverrideRuleStatus,
} from '../../PlanOverrides/pages/PlanBenefitOverrides/enums/plan-benefit-override-rule.enum'
import {
  useFindOverrideRuleByMatch,
  FIND_OVERRIDE_RULE_QUERY_KEY,
} from '../../PlanOverrides/pages/PlanBenefitOverrides/hooks/useFindOverrideRuleByMatch'
import type { PlanBenefitOverrideRuleResponseDto } from '../../PlanOverrides/api/dto/plan-benefit-override-rules.dto'
import {
  FIELD_CONFIG,
  IDENTITY_FIELDS,
  coerceParamValue,
  type FieldValueType,
} from '../utils/planBenefitFields'
import { useEditDocumentStaging } from '../../PlanOverrides/hooks/useEditDocumentStaging'
import {
  ALLOWED_MIME_TYPES,
  MAX_SIZE_BY_MIME,
} from '../../PlanOverrides/utils/fileValidation'
import { InlineEvidenceSection } from './InlineEvidenceSection'

// ---------------------------------------------------------------------------
// Types & config
// ---------------------------------------------------------------------------

const SRC = PlanBenefitOverrideValueSource

type RowState = {
  source: PlanBenefitOverrideValueSource
  overrideValue: string | number | boolean | null
}

export interface ReviewCellInfo {
  field: string
  rowData: Record<string, unknown>
}

export interface ReviewSavedUpdate {
  params: Record<string, unknown>
  paramSources: Record<string, string>
  status: string
}

interface Props {
  open: boolean
  onClose: () => void
  cellInfo: ReviewCellInfo | null
  onSaved?: (rowId: string, update: ReviewSavedUpdate) => void
}

// ---------------------------------------------------------------------------
// Theme tokens
// ---------------------------------------------------------------------------

const BRAND = '#0d9488'
const BRAND_LIGHT = '#ccfbf1'
const HIGHLIGHT_ROW = '#fefce8'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const currencyFormatter = (v: number | string | undefined): string => {
  if (v === undefined || v === null || v === '') return ''
  const n = typeof v === 'string' ? Number(v) : v
  if (isNaN(n)) return String(v)
  if (n === -1) return '-1'
  return n.toLocaleString('en-US')
}

const currencyParser = (v: string | undefined): number | string => {
  if (!v) return ''
  const s = v.replace(/[$,\s]/g, '')
  if (s === '' || s === '-') return s
  const n = Number(s)
  return isNaN(n) ? s : n
}

function formatValue(value: unknown, valueType: FieldValueType): string {
  if (valueType === 'boolean') return value ? 'Yes' : 'No'
  if (value === null || value === undefined || value === '') return '—'
  if (valueType === 'currency' || valueType === 'number') {
    const n = Number(value)
    if (!isNaN(n)) {
      // Trim trailing zeros (e.g. "-1.000000" -> "-1") and add separators.
      return n.toLocaleString('en-US', { maximumFractionDigits: 2 })
    }
  }
  return String(value)
}

function ReadonlyBoolCell({ value }: { value: unknown }) {
  return (
    <span style={{ fontSize: 13, fontWeight: 600 }}>
      {value ? 'Yes' : 'No'}
    </span>
  )
}

function buildInitialRows(
  fields: string[],
  rule: PlanBenefitOverrideRuleResponseDto | null,
  ideonData: Record<string, unknown>
): Record<string, RowState> {
  return Object.fromEntries(
    fields.map((field) => {
      const recordedSource = rule?.paramSources?.[field]
      const source: PlanBenefitOverrideValueSource =
        recordedSource ?? PlanBenefitOverrideValueSource.IDEON_DATA
      const isBool = FIELD_CONFIG[field]?.valueType === 'boolean'
      const fallback = isBool
        ? Boolean(ideonData[field] ?? false)
        : ((ideonData[field] as string | number | boolean | null) ?? null)
      const overrideValue =
        rule?.params?.[field] !== undefined
          ? (rule.params[field] as string | number | boolean | null)
          : fallback
      return [field, { source, overrideValue }]
    })
  )
}

const COL_LABEL_STYLE: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: '#94a3b8',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
}

// ---------------------------------------------------------------------------
// OverrideInput
// ---------------------------------------------------------------------------

interface OverrideInputProps {
  field: string
  value: string | number | boolean | null
  disabled: boolean
  autoFocus?: boolean
  onChange: (v: string | number | boolean | null) => void
}

function OverrideInput({
  field,
  value,
  disabled,
  autoFocus,
  onChange,
}: OverrideInputProps) {
  const cfg = FIELD_CONFIG[field]
  const valueType = cfg?.valueType ?? 'string'
  const inputRef = useRef<any>(null)

  // When the field is just switched to Override, focus the input and select its
  // contents so the user can type a new value over the prefilled default
  // without having to clear it manually.
  useEffect(() => {
    if (!autoFocus || disabled || valueType === 'boolean') return
    const id = window.setTimeout(() => {
      inputRef.current?.focus?.({ cursor: 'all' })
      const active = document.activeElement
      if (active instanceof HTMLInputElement) active.select()
    }, 0)
    return () => window.clearTimeout(id)
  }, [autoFocus, disabled, valueType])

  if (valueType === 'boolean') {
    return (
      <Switch
        checked={value === true}
        disabled={disabled}
        onChange={(checked) => onChange(checked)}
        size="small"
      />
    )
  }
  if (valueType === 'currency') {
    return (
      <InputNumber
        ref={inputRef}
        value={value as number | null}
        disabled={disabled}
        onChange={(v) => onChange(v ?? null)}
        formatter={currencyFormatter}
        parser={currencyParser as any}
        size="small"
        style={{ flex: 1, minWidth: 0, width: '100%' }}
        min={-1}
      />
    )
  }
  if (valueType === 'number') {
    return (
      <InputNumber
        ref={inputRef}
        value={value as number | null}
        disabled={disabled}
        onChange={(v) => onChange(v ?? null)}
        size="small"
        style={{ flex: 1, minWidth: 0, width: '100%' }}
        min={-1}
      />
    )
  }
  return (
    <Input
      ref={inputRef}
      value={(value as string) ?? ''}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      size="small"
      style={{ flex: 1, minWidth: 0, width: '100%' }}
    />
  )
}

// ---------------------------------------------------------------------------
// FieldRow — click a value cell to choose its source
// ---------------------------------------------------------------------------

const PILL_BASE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  borderRadius: 7,
  padding: '4px 8px',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 1.2,
}

const SELECTED_STYLE: React.CSSProperties = {
  ...PILL_BASE,
  background: '#ecfdf5',
  border: '1px solid #a7f3d0',
  color: '#15803d',
}

const UNSELECTED_STYLE: React.CSSProperties = {
  ...PILL_BASE,
  background: '#f8fafc',
  border: '1px solid #e5e7eb',
  color: '#94a3b8',
}

interface FieldRowProps {
  field: string
  isHighlighted: boolean
  isChecked: boolean
  isReadOnly: boolean
  rowState: RowState
  ideonValue: unknown
  realValue: unknown
  showRealData: boolean
  autoFocusOverride: boolean
  onToggle: (field: string, checked: boolean) => void
  onSelect: (field: string, src: PlanBenefitOverrideValueSource) => void
  onOverrideChange: (field: string, v: string | number | boolean | null) => void
}

function FieldRow({
  field,
  isHighlighted,
  isChecked,
  isReadOnly,
  rowState,
  ideonValue,
  realValue,
  showRealData,
  autoFocusOverride,
  onToggle,
  onSelect,
  onOverrideChange,
}: FieldRowProps) {
  const cfg = FIELD_CONFIG[field]
  const label = cfg?.label ?? field
  const valueType = cfg?.valueType ?? 'string'
  const { source, overrideValue } = rowState

  const isDisabled = isReadOnly || !isChecked

  const cellStyle = (src: PlanBenefitOverrideValueSource) =>
    source === src ? SELECTED_STYLE : UNSELECTED_STYLE

  const renderDisplayVal = (val: unknown) =>
    valueType === 'boolean' ? (
      <ReadonlyBoolCell value={val ?? false} />
    ) : (
      <span>{formatValue(val, valueType)}</span>
    )

  return (
    <tr
      style={{
        background: isHighlighted ? HIGHLIGHT_ROW : '#fff',
        borderTop: '1px solid #f1f5f9',
        opacity: isChecked ? 1 : 0.45,
        transition: 'opacity 150ms ease',
      }}
    >
      <td
        style={{
          padding: '8px 12px',
          verticalAlign: 'middle',
          width: 36,
          textAlign: 'center',
        }}
      >
        <input
          type="checkbox"
          checked={isChecked}
          disabled={isReadOnly}
          onChange={(e) => onToggle(field, e.target.checked)}
          style={{
            cursor: isReadOnly ? 'default' : 'pointer',
            width: 14,
            height: 14,
          }}
        />
      </td>
      <td style={{ padding: '8px 12px', verticalAlign: 'middle' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>
          {label}
        </div>
        {label.toLowerCase().replace(/\s+/g, '') !== field.toLowerCase() && (
          <div style={{ fontSize: 11, color: '#9ca3af' }}>{field}</div>
        )}
      </td>
      <td
        style={{
          padding: '8px 12px',
          verticalAlign: 'middle',
          cursor: isDisabled ? 'default' : undefined,
        }}
        onClick={() => !isDisabled && onSelect(field, SRC.IDEON_DATA)}
      >
        <div style={cellStyle(SRC.IDEON_DATA)}>
          {source === SRC.IDEON_DATA && (
            <CheckCircleFilled style={{ color: '#16a34a', fontSize: 13 }} />
          )}
          {renderDisplayVal(ideonValue)}
        </div>
      </td>
      {showRealData && (
        <td
          style={{
            padding: '8px 12px',
            verticalAlign: 'middle',
            cursor: isDisabled ? 'default' : undefined,
          }}
          onClick={() => !isDisabled && onSelect(field, SRC.REAL_DATA)}
        >
          <div style={cellStyle(SRC.REAL_DATA)}>
            {source === SRC.REAL_DATA && (
              <CheckCircleFilled style={{ color: '#16a34a', fontSize: 13 }} />
            )}
            {renderDisplayVal(realValue)}
          </div>
        </td>
      )}
      <td
        style={{
          padding: '8px 12px',
          verticalAlign: 'middle',
          minWidth: 150,
          cursor: isDisabled ? 'default' : undefined,
        }}
        onClick={() =>
          !isDisabled &&
          source !== SRC.OVERRIDE_DATA &&
          onSelect(field, SRC.OVERRIDE_DATA)
        }
      >
        <div
          style={{
            ...(source === SRC.OVERRIDE_DATA
              ? SELECTED_STYLE
              : UNSELECTED_STYLE),
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          {source === SRC.OVERRIDE_DATA ? (
            <CheckCircleFilled
              style={{ color: '#16a34a', fontSize: 13, flexShrink: 0 }}
            />
          ) : (
            <MinusCircleOutlined
              style={{ color: '#d1d5db', fontSize: 13, flexShrink: 0 }}
            />
          )}
          <OverrideInput
            field={field}
            value={source === SRC.OVERRIDE_DATA ? overrideValue : null}
            disabled={source !== SRC.OVERRIDE_DATA || isDisabled}
            autoFocus={autoFocusOverride && !isDisabled}
            onChange={(v) => onOverrideChange(field, v)}
          />
        </div>
      </td>
    </tr>
  )
}

// ---------------------------------------------------------------------------
// Status pill
// ---------------------------------------------------------------------------

function StatusPill({ status }: { status: string }) {
  const isApproved = status === PlanBenefitOverrideRuleStatus.APPROVED
  const isRejected = status === PlanBenefitOverrideRuleStatus.REJECTED
  const dotColor = isApproved ? '#16a34a' : isRejected ? '#dc2626' : '#f59e0b'
  const bg = isApproved ? '#f0fdf4' : isRejected ? '#fef2f2' : '#fffbeb'
  const border = isApproved ? '#bbf7d0' : isRejected ? '#fecaca' : '#fde68a'
  const label = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: 6,
        padding: '2px 10px',
        fontSize: 13,
        fontWeight: 600,
        color: '#374151',
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          background: dotColor,
        }}
      />
      {label}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Main modal
// ---------------------------------------------------------------------------

export default function PlanBenefitReviewModal({
  open,
  onClose,
  cellInfo,
  onSaved,
}: Props) {
  const queryClient = useQueryClient()

  const identity = useMemo(() => {
    if (!cellInfo) return null
    const d = cellInfo.rowData
    const year = d.year as number
    const hiosPlanId = d.hiosPlanId as string
    const benefitName = d.benefit as string
    return { year, hiosPlanId, benefitName }
  }, [cellInfo])

  const { rule, isLoading: ruleLoading } = useFindOverrideRuleByMatch(
    open ? identity : null
  )

  const fields = useMemo(() => {
    if (!cellInfo) return []
    return Object.keys(FIELD_CONFIG).filter((f) => !IDENTITY_FIELDS.has(f))
  }, [cellInfo])

  const ideonData = cellInfo?.rowData ?? {}
  const realData = (cellInfo?.rowData?._currentValue ?? {}) as Record<
    string,
    unknown
  >

  const [rows, setRows] = useState<Record<string, RowState>>({})
  const [_initialRows, setInitialRows] = useState<Record<string, RowState>>({})
  const [focusField, setFocusField] = useState<string | null>(null)
  const [checkedFields, setCheckedFields] = useState<Set<string>>(new Set())
  const rowsInitializedRef = useRef(false)

  useEffect(() => {
    if (!open) {
      rowsInitializedRef.current = false
      return
    }
    if (ruleLoading || fields.length === 0 || rowsInitializedRef.current) return
    const built = buildInitialRows(fields, rule, cellInfo?.rowData ?? {})
    setRows(built)
    setInitialRows(built)
    setCheckedFields(new Set(Object.keys(rule?.params ?? {})))
    rowsInitializedRef.current = true
  }, [open, ruleLoading, rule, fields, cellInfo])

  const staging = useEditDocumentStaging({ ruleId: rule?.id ?? '' })
  const [localFiles, setLocalFiles] = useState<File[]>([])

  const handleAddLocalFiles = useCallback((incoming: File[]) => {
    const valid = incoming.filter((f) => {
      if (!ALLOWED_MIME_TYPES.has(f.type)) {
        message.error(`${f.name}: unsupported file type.`)
        return false
      }
      const max = MAX_SIZE_BY_MIME[f.type] ?? 0
      if (f.size > max) {
        message.error(`${f.name}: file too large.`)
        return false
      }
      return true
    })
    setLocalFiles((prev) => {
      const names = new Set(prev.map((x) => x.name))
      return [...prev, ...valid.filter((x) => !names.has(x.name))].slice(0, 3)
    })
  }, [])

  const [isSaving, setIsSaving] = useState(false)
  const savingRef = useRef(false)

  const handleSelect = useCallback(
    (field: string, src: PlanBenefitOverrideValueSource) => {
      setFocusField(src === SRC.OVERRIDE_DATA ? field : null)
      if (src === SRC.OVERRIDE_DATA) {
        setRows((prev) => {
          const existing = prev[field]
          const prefill =
            existing?.overrideValue !== null &&
            existing?.overrideValue !== undefined
              ? existing.overrideValue
              : ((cellInfo?.rowData[field] as
                  | string
                  | number
                  | boolean
                  | null) ?? null)
          return {
            ...prev,
            [field]: { source: src, overrideValue: prefill },
          }
        })
        return
      }
      if (!cellInfo) return
      const d = cellInfo.rowData
      const rawVal =
        src === SRC.IDEON_DATA
          ? d[field]
          : (d._currentValue as Record<string, unknown> | undefined)?.[field]
      setRows((prev) => ({
        ...prev,
        [field]: { source: src, overrideValue: rawVal as any },
      }))
    },
    [cellInfo]
  )

  const handleOverrideChange = useCallback(
    (field: string, v: string | number | boolean | null) => {
      setRows((prev) => ({
        ...prev,
        [field]: { ...prev[field], overrideValue: v },
      }))
    },
    []
  )

  const isApproved = rule?.status === PlanBenefitOverrideRuleStatus.APPROVED

  const handleToggle = useCallback(
    (field: string, checked: boolean) => {
      if (isApproved) return
      setCheckedFields((prev) => {
        const next = new Set(prev)
        checked ? next.add(field) : next.delete(field)
        return next
      })
    },
    [isApproved]
  )

  const handleClose = useCallback(() => {
    if (isSaving) return
    setRows({})
    setInitialRows({})
    setLocalFiles([])
    setFocusField(null)
    setCheckedFields(new Set())
    staging.discard()
    onClose()
  }, [isSaving, staging, onClose])

  const save = useCallback(
    async (targetStatus: PlanBenefitOverrideRuleStatus) => {
      if (!cellInfo || !identity || savingRef.current) return
      savingRef.current = true
      setIsSaving(true)
      try {
        const selectedParams: Record<string, unknown> = {}
        const selectedSources: Record<string, PlanBenefitOverrideValueSource> =
          {}
        for (const [field, rs] of Object.entries(rows)) {
          if (!checkedFields.has(field)) continue
          let rawValue: unknown
          if (rs.source === SRC.IDEON_DATA) {
            rawValue = cellInfo.rowData[field]
          } else if (rs.source === SRC.REAL_DATA) {
            const live = cellInfo.rowData._currentValue as
              | Record<string, unknown>
              | undefined
            rawValue = live?.[field] ?? null
          } else {
            rawValue = rs.overrideValue
          }
          selectedParams[field] = coerceParamValue(field, rawValue)
          selectedSources[field] = rs.source
        }

        // When approving, fill every FIELD_CONFIG field not covered by the
        // modal's selections — mirrors the row approve icon behaviour.
        if (targetStatus === PlanBenefitOverrideRuleStatus.APPROVED) {
          const existingOverrideParams = rule?.params ?? {}
          for (const field of Object.keys(FIELD_CONFIG)) {
            if (IDENTITY_FIELDS.has(field) || field in selectedParams) continue
            if (existingOverrideParams[field] !== undefined) {
              selectedParams[field] = coerceParamValue(
                field,
                existingOverrideParams[field]
              )
              selectedSources[field] =
                (
                  rule?.paramSources as
                    | Record<
                        string,
                        | typeof SRC.OVERRIDE_DATA
                        | typeof SRC.IDEON_DATA
                        | typeof SRC.REAL_DATA
                      >
                    | undefined
                )?.[field] ?? SRC.OVERRIDE_DATA
            } else if (cellInfo.rowData[field] !== undefined) {
              selectedParams[field] = coerceParamValue(
                field,
                cellInfo.rowData[field]
              )
              selectedSources[field] = SRC.IDEON_DATA
            }
          }
        }

        const mergedParams = { ...(rule?.params ?? {}), ...selectedParams }
        const mergedSources = {
          ...(rule?.paramSources ?? {}),
          ...selectedSources,
        }

        // Remove fields that were unchecked — they should no longer exist in params
        for (const field of Object.keys(rule?.params ?? {})) {
          if (!checkedFields.has(field)) {
            delete mergedParams[field]
            delete mergedSources[field]
          }
        }

        let ruleId: string
        if (!rule) {
          const created = await createOverrideRule({
            template: PlanBenefitOverrideRuleTemplateKey.PARTIAL_FIELD_UPDATE,
            match: {
              year: identity.year,
              hiosPlanId: identity.hiosPlanId,
              benefitName: identity.benefitName,
            },
            params: mergedParams,
            paramSources: mergedSources,
          })
          ruleId = created.id
        } else {
          await updateOverrideRule(rule.id, {
            params: mergedParams,
            paramSources: mergedSources,
          })
          ruleId = rule.id
        }

        const currentRuleStatus =
          rule?.status ?? PlanBenefitOverrideRuleStatus.DRAFT
        if (targetStatus !== currentRuleStatus) {
          await updateOverrideRuleStatus(ruleId, { status: targetStatus })
        }

        if (rule && staging.hasStagedChanges) {
          await staging.commit()
        } else if (localFiles.length > 0) {
          await Promise.all(
            localFiles.map(async (file) => {
              const { documentId, presignedUploadUrl: uploadUrl } =
                await presignUploadUrl(ruleId, {
                  fileName: file.name,
                  contentType: file.type,
                  fileSize: file.size,
                })
              const result = await uploadToS3({
                presignedUrl: uploadUrl,
                file,
                contentType: file.type,
              })
              if (!result.success)
                throw result.error ?? new Error('S3 upload failed')
              await completeUpload(ruleId, documentId, { sizeBytes: file.size })
            })
          )
        }

        await queryClient.invalidateQueries({
          queryKey: [FIND_OVERRIDE_RULE_QUERY_KEY],
        })
        const rowId = cellInfo.rowData.id as string
        onSaved?.(rowId, {
          params: mergedParams,
          paramSources: mergedSources as Record<string, string>,
          status: targetStatus,
        })
        handleClose()
        const verb =
          targetStatus === PlanBenefitOverrideRuleStatus.APPROVED
            ? 'approved'
            : targetStatus === PlanBenefitOverrideRuleStatus.REJECTED
              ? 'rejected'
              : 'saved'
        message.success(`Override ${verb}.`)
      } catch {
        message.error('Failed to save override. Please try again.')
      } finally {
        savingRef.current = false
        setIsSaving(false)
      }
    },
    [
      cellInfo,
      identity,
      rows,
      rule,
      staging,
      localFiles,
      queryClient,
      onSaved,
      handleClose,
    ]
  )

  useEffect(() => {
    if (!open) return
    const html = document.documentElement
    const orig = html.style.overflow
    html.style.overflow = 'hidden'
    return () => {
      html.style.overflow = orig
    }
  }, [open])

  const hasRealData = useMemo(
    () =>
      fields.some((f) => {
        const v = realData[f]
        return v !== null && v !== undefined && v !== ''
      }),
    [fields, realData]
  )

  const statusLabel = rule?.status ?? 'Draft'

  const approveBtn = (label = 'Approve Row as Override') => (
    <Button
      type="primary"
      size="small"
      icon={<CheckOutlined />}
      style={{ background: '#10b981', borderColor: '#10b981' }}
      onClick={() => save(PlanBenefitOverrideRuleStatus.APPROVED)}
      loading={isSaving}
      disabled={isSaving}
    >
      {label}
    </Button>
  )

  const saveDraftBtn = (label = 'Save') => (
    <Button
      size="small"
      icon={<SendOutlined />}
      onClick={() => save(PlanBenefitOverrideRuleStatus.DRAFT)}
      loading={isSaving}
      disabled={isSaving}
    >
      {label}
    </Button>
  )

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title={null}
      closable={false}
      width={680}
      footer={null}
      centered
      destroyOnClose
      transitionName="ant-fade"
      maskTransitionName="ant-fade"
      styles={{
        body: {
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '82vh',
          overflow: 'hidden',
        },
      }}
    >
      {ruleLoading && open ? (
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Spin />
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
          }}
        >
          {/* ── Top pinned: header + status strip ── */}
          <div style={{ padding: '12px 14px 0', flexShrink: 0 }}>
            {/* ── Header ── */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 8,
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: BRAND_LIGHT,
                  color: BRAND,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 15,
                  flexShrink: 0,
                }}
              >
                <TableOutlined />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    fontSize: 11,
                    fontWeight: 600,
                    color: BRAND,
                    marginBottom: 1,
                  }}
                >
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: BRAND,
                    }}
                  />
                  Plan Benefits
                </div>
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: '#0f172a',
                    lineHeight: 1.25,
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 6,
                    flexWrap: 'wrap',
                  }}
                >
                  {identity
                    ? `${identity.hiosPlanId} - ${identity.benefitName}`
                    : 'Override'}
                  {identity && (
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 500,
                        color: '#94a3b8',
                      }}
                    >
                      {identity.year}
                    </span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={handleClose}
                aria-label="Close"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#94a3b8',
                  fontSize: 14,
                  padding: 2,
                  flexShrink: 0,
                }}
              >
                <CloseOutlined />
              </button>
            </div>

            {/* ── Status strip ── */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 0,
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                padding: '6px 10px',
                marginBottom: 8,
                background: '#f8fafc',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={COL_LABEL_STYLE}>Status</span>
                <StatusPill status={statusLabel} />
              </div>
            </div>
          </div>
          {/* end top pinned */}

          {/* ── Scrollable: comparison table ── */}
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              padding: '8px 14px 0',
            }}
          >
            <div
              style={{
                flex: 1,
                border: '1px solid #e5e7eb',
                borderRadius: 10,
                overflowY: 'auto',
                marginBottom: 10,
              }}
            >
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                }}
              >
                <thead
                  style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    background: '#f8fafc',
                  }}
                >
                  <tr style={{ background: '#f8fafc' }}>
                    <th
                      style={{
                        ...COL_LABEL_STYLE,
                        padding: '8px 12px',
                        width: 36,
                        textAlign: 'center',
                      }}
                    >
                      Overridden
                    </th>
                    <th
                      style={{
                        ...COL_LABEL_STYLE,
                        padding: '8px 12px',
                        textAlign: 'left',
                      }}
                    >
                      Benefit Field
                    </th>
                    <th
                      style={{
                        ...COL_LABEL_STYLE,
                        padding: '8px 12px',
                        textAlign: 'left',
                        minWidth: 100,
                      }}
                    >
                      Ideon Record
                    </th>
                    {hasRealData && (
                      <th
                        style={{
                          ...COL_LABEL_STYLE,
                          padding: '8px 12px',
                          textAlign: 'left',
                          minWidth: 100,
                        }}
                      >
                        Sureco Record
                      </th>
                    )}
                    <th
                      style={{
                        ...COL_LABEL_STYLE,
                        padding: '8px 12px',
                        textAlign: 'left',
                        minWidth: 150,
                      }}
                    >
                      Change Record
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {fields.map((field) => (
                    <FieldRow
                      key={field}
                      field={field}
                      isHighlighted={field === cellInfo?.field}
                      isChecked={checkedFields.has(field)}
                      isReadOnly={isApproved}
                      rowState={
                        rows[field] ?? {
                          source: SRC.IDEON_DATA,
                          overrideValue: null,
                        }
                      }
                      ideonValue={ideonData[field]}
                      realValue={realData[field]}
                      showRealData={hasRealData}
                      autoFocusOverride={focusField === field}
                      onToggle={handleToggle}
                      onSelect={handleSelect}
                      onOverrideChange={handleOverrideChange}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {/* end scrollable */}

          {/* ── Bottom pinned: info + evidence + footer ── */}
          <div style={{ flexShrink: 0, padding: '0 14px 14px' }}>
            {/* ── Info line ── */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 11,
                color: '#94a3b8',
                marginBottom: 8,
                marginTop: 6,
              }}
            >
              <InfoCircleOutlined />
              Click a value to choose its source for each field, or pick
              Override to enter a custom value.
            </div>

            {/* ── Evidence ── */}
            <div style={{ marginBottom: 10 }}>
              <InlineEvidenceSection
                ruleId={rule?.id ?? null}
                staging={staging}
                localFiles={localFiles}
                isReadOnly={isApproved}
                onAddLocal={handleAddLocalFiles}
                onRemoveLocal={(idx) =>
                  setLocalFiles((p) => p.filter((_, i) => i !== idx))
                }
              />
            </div>

            {/* ── Footer ── */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                borderTop: '1px solid #e5e7eb',
                paddingTop: 14,
              }}
            >
              {isApproved ? (
                <>
                  <span
                    style={{
                      fontSize: 12,
                      color: '#94a3b8',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      flex: 1,
                    }}
                  >
                    <CheckOutlined style={{ color: '#16a34a' }} />
                    Approved — read only
                  </span>
                  {saveDraftBtn('Send back to draft')}
                </>
              ) : (
                <>
                  {approveBtn()}
                  {saveDraftBtn()}
                </>
              )}
            </div>
          </div>
          {/* end bottom pinned */}
        </div>
      )}
    </Modal>
  )
}
